import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import { fmtDate } from "@/lib/dates";

const ROLE_STYLES = {
  supervisor: "bg-neutral-900 text-neutral-50",
  admin: "bg-indigo-100 text-indigo-900",
  storekeeper: "bg-sky-100 text-sky-900",
  engineer: "bg-emerald-100 text-emerald-900",
};

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([api.get("/users"), api.get("/regions")]);
      setUsers(u.data);
      setRegions(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen">
      <div className="border-b border-neutral-200 bg-white/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="px-10 py-6 flex items-end justify-between">
          <div>
            <div className="label-eyebrow">Access</div>
            <h1 className="font-display text-4xl tracking-tight leading-none mt-2">Office Users</h1>
            <p className="text-sm text-neutral-500 mt-2 max-w-lg">Supervisors, admins and storekeepers. Field engineers &amp; technicians live on the <span className="font-mono">Technicians</span> tab.</p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="new-user-btn" className="h-10 rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
            <Plus size={16} weight="bold" className="mr-2" /> Add User
          </Button>
        </div>
      </div>

      <div className="p-10">
        <div className="border border-neutral-200 bg-white">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">Name</th>
                <th className="px-4 py-3 label-eyebrow">Username</th>
                <th className="px-4 py-3 label-eyebrow">Email</th>
                <th className="px-4 py-3 label-eyebrow">Role</th>
                <th className="px-4 py-3 label-eyebrow">Region</th>
                <th className="px-4 py-3 label-eyebrow">Created</th>
                <th className="px-4 py-3 label-eyebrow">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-neutral-400">Loading…</td></tr>}
              {!loading && users.map((u) => (
                <tr key={u.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{u.username || <span className="text-neutral-400">—</span>}</td>
                  <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${ROLE_STYLES[u.role]}`}>{u.role}</span>
                  </td>
                  <td className="px-4 py-3 text-neutral-600">{u.region || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-neutral-500">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => setEditing(u)} className="p-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white" title="Edit" data-testid={`edit-user-${u.id}`}>
                        <PencilSimple size={14} weight="bold" />
                      </button>
                      {u.id !== me.id && (
                        <button onClick={() => del(u.id)} className="p-1.5 border border-neutral-300 hover:border-red-600 hover:bg-red-600 hover:text-white" title="Delete" data-testid={`delete-user-${u.id}`}>
                          <Trash size={14} weight="bold" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <UserDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        regions={regions}
        onSaved={() => { setShowCreate(false); load(); }}
      />
      {editing && (
        <UserDialog
          open={!!editing}
          onClose={() => setEditing(null)}
          user={editing}
          regions={regions}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function UserDialog({ open, onClose, user, regions, onSaved }) {
  const isEdit = !!user;
  const [name, setName] = useState(user?.name || "");
  const [username, setUsername] = useState(user?.username || "");
  const [email, setEmail] = useState(user?.email || "");
  const [role, setRole] = useState(user?.role || "admin");
  const [region, setRegion] = useState(user?.region || "");
  const [password, setPassword] = useState("");
  // permissions: null → use role defaults; array → explicit override
  const [permsOverride, setPermsOverride] = useState(user?.permissions ?? null);
  const [busy, setBusy] = useState(false);
  const [perms, setPerms] = useState([]);        // catalogue
  const [roleDefaults, setRoleDefaults] = useState({});

  useEffect(() => {
    let alive = true;
    api.get("/auth/permissions").then((r) => {
      if (!alive) return;
      setPerms(r.data.permissions || []);
      setRoleDefaults(r.data.role_defaults || {});
    }).catch(() => { /* ignore */ });
    return () => { alive = false; };
  }, []);

  // The set of permissions actually checked in the UI.
  const effectiveList = permsOverride ?? (roleDefaults[role] || []);
  const effectiveSet = new Set(effectiveList);
  const usingDefaults = permsOverride === null;

  const togglePerm = (key) => {
    // First time editing → snapshot the defaults so we don't lose them.
    const base = permsOverride ?? (roleDefaults[role] || []);
    const next = new Set(base);
    if (next.has(key)) next.delete(key); else next.add(key);
    setPermsOverride(Array.from(next));
  };

  const resetToDefaults = () => setPermsOverride(null);

  const submit = async () => {
    if (!name || !email) return toast.error("Name and email required");
    if (!username.trim()) return toast.error("Username required");
    if (!isEdit && !password) return toast.error("Password required");
    setBusy(true);
    try {
      const payload = {
        name,
        username: username.trim().toLowerCase(),
        role,
        region: region || null,
        permissions: permsOverride, // null (defaults) or explicit list
      };
      if (password) payload.password = password;
      if (isEdit) {
        await api.patch(`/users/${user.id}`, payload);
      } else {
        await api.post("/users", { ...payload, email, password });
      }
      toast.success(isEdit ? "Updated" : "Created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-none border-neutral-900 max-h-[92vh] overflow-y-auto" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">{isEdit ? "Edit User" : "Add User"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Full name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-none border-neutral-400" data-testid="user-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Username (login ID)</Label>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="rounded-none border-neutral-400 font-mono lowercase"
                data-testid="user-username"
                placeholder="john.smith"
                autoCapitalize="none"
                autoCorrect="off"
              />
              <p className="text-[10px] text-neutral-500 font-mono">3–32 chars · a-z, 0-9, . _ -</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={isEdit} className="rounded-none border-neutral-400 disabled:bg-neutral-100" data-testid="user-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">{isEdit ? "New password (leave blank to keep)" : "Password"}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none border-neutral-400" data-testid="user-password" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Role (defaults)</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="user-role" className="rounded-none border-neutral-400"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="manager">Manager (full access)</SelectItem>
                  <SelectItem value="admin">Admin (helpdesk)</SelectItem>
                  <SelectItem value="storekeeper">Storekeeper</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-neutral-500 font-mono">Office roles only. Add field staff on the Technicians page.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Region (optional)</Label>
              <Select value={region || "none"} onValueChange={(v) => setRegion(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="user-region" className="rounded-none border-neutral-400"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {regions.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Permissions checklist */}
          <div className="space-y-2 border-t border-neutral-200 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="label-eyebrow">Permissions</Label>
                <div className="text-[10px] text-neutral-500 font-mono mt-0.5">
                  {usingDefaults
                    ? "Using role defaults. Tick / untick to customise."
                    : "Custom permission set (overrides role defaults)."}
                </div>
              </div>
              {!usingDefaults && (
                <button type="button" onClick={resetToDefaults} data-testid="reset-perms" className="text-xs font-medium underline underline-offset-4 hover:text-neutral-500">
                  Reset to role defaults
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border border-neutral-200 bg-neutral-50 p-3">
              {perms.map((p) => {
                const checked = effectiveSet.has(p.key);
                const isDefault = (roleDefaults[role] || []).includes(p.key);
                return (
                  <label key={p.key} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-white px-2 py-1" data-testid={`perm-row-${p.key}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglePerm(p.key)}
                      data-testid={`perm-${p.key}`}
                      className="w-4 h-4 accent-neutral-900"
                    />
                    <span className="flex-1">{p.label}</span>
                    {isDefault && <span className="text-[9px] font-mono uppercase tracking-wider bg-neutral-200 text-neutral-700 px-1 py-0.5">default</span>}
                  </label>
                );
              })}
            </div>
            <div className="text-[10px] text-neutral-500 font-mono">
              Print and view-own records are always available. Supervisors always have every permission.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="user-submit" className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
            {busy ? "Saving…" : (isEdit ? "Save Changes" : "Create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
